import React, { useMemo, useState } from "react";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { Autocomplete } from "@material-ui/lab";

import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { SaleAction } from "actions";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  buildCopyBidderSaleOptions,
  CopyBidderDirection,
  CopyBidderSaleOption,
  getSaleName,
} from "components/CopyBidderRegistrations/lib";
import SlimButton from "components/Button/SlimButton";
import { Button, SecondaryButton } from "components/Form";
import { Label } from "components/Form/FormikControls";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { ModalTypes } from "constants/navigation";
import { LOAD_MORE_SALES } from "constants/sale";
import { Settings } from "constants/settings";

import {
  getContextByModalType,
  getCurrentSale,
  getIsFetchingSales,
  getIsOnline,
  getSales,
  getSetting,
  selectHasWriteAccessBySaleIdLookup,
} from "selectors";

interface CopyBidderRegistrationsDialogContext {
  direction?: CopyBidderDirection;
}

interface CopyBidderRegistrationsDialogProps {
  onClose: () => void;
}

const SaleOptionCard = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.space[1]}px 0;
`;

const SaleOptionTitle = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const SaleOptionDetail = styled.div`
  color: ${({ theme }) => theme.colors.gray40};
  font-size: ${({ theme }) => theme.fontSizes.beta}px;
`;

const HelperText = styled.p`
  margin: 0 0 ${({ theme }) => theme.space[2]}px;
`;

function renderSaleOption(saleOption: CopyBidderSaleOption) {
  return (
    <SaleOptionCard>
      <SaleOptionTitle>{saleOption.title}</SaleOptionTitle>
      <SaleOptionDetail>{saleOption.detail}</SaleOptionDetail>
    </SaleOptionCard>
  );
}

export function CopyBidderRegistrationsDialog({
  onClose,
}: CopyBidderRegistrationsDialogProps) {
  const dispatch = useDispatch();
  const { direction = CopyBidderDirection.TO } =
    useSelector<RootState, CopyBidderRegistrationsDialogContext | undefined>(
      getContextByModalType(ModalTypes.CopyBidderRegistrations),
    ) || {};
  const currentSale = useSelector<RootState, Sale | null>(getCurrentSale);
  const sales = useSelector<RootState, Record<string | number, Sale>>(getSales);
  const hasWriteAccessBySaleId = useSelector<
    RootState,
    Record<string | number, boolean>
  >(selectHasWriteAccessBySaleIdLookup);
  const isFetchingSales = useSelector<RootState, boolean>(getIsFetchingSales);
  const isOnline = useSelector<RootState, boolean>(getIsOnline);
  const allSalesLoaded = useSelector<RootState, boolean>(
    getSetting(Settings.allSalesLoaded),
  );

  const [selectedSaleId, setSelectedSaleId] = useState<number | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const currentLivestockSaleId = currentSale?.livestocksale_id || null;

  const saleOptions = useMemo(
    () =>
      buildCopyBidderSaleOptions({
        sales,
        currentLivestockSaleId,
        direction,
        hasWriteAccessBySaleId,
      }),
    [sales, currentLivestockSaleId, direction, hasWriteAccessBySaleId],
  );

  const selectedSaleOption =
    saleOptions.find(saleOption => saleOption.value === selectedSaleId) || null;

  if (!currentSale) {
    return null;
  }

  const isCopyFrom = direction === CopyBidderDirection.FROM;
  const currentSaleName = getSaleName(currentSale);
  const title = isCopyFrom
    ? "Copy Bidders From Another Sale"
    : "Copy Bidders To Another Sale";
  const currentSaleText = <strong>{currentSaleName}</strong>;
  const helperText = isCopyFrom ? (
    <>
      All bidder registrations, including delivery and postal addresses, will be
      copied from the selected sale into {currentSaleText}.
    </>
  ) : (
    <>
      All bidder registrations, including delivery and postal addresses, will be
      copied from {currentSaleText} into the selected sale.
    </>
  );
  const tooltip = isCopyFrom
    ? "Sales using registered bidders are available to be selected."
    : "Sales using registered bidders that you can edit are available to be selected.";

  const fromSaleName = isCopyFrom ? selectedSaleOption?.label : currentSaleName;
  const toSaleName = isCopyFrom ? currentSaleName : selectedSaleOption?.label;

  function loadAllSales() {
    dispatch(SaleAction.requestChunked({ fetchAll: true }));
  }

  function onConfirm() {
    if (selectedSaleId === null || currentLivestockSaleId === null) {
      return;
    }
    const [fromSaleId, toSaleId] = isCopyFrom
      ? [selectedSaleId, currentLivestockSaleId]
      : [currentLivestockSaleId, selectedSaleId];

    dispatch(SaleAction.copyBidderRegistrations(fromSaleId, toSaleId));
    setIsConfirmOpen(false);
    onClose();
  }

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle onClose={onClose}>{title}</DialogTitle>
        <DialogContent dividers>
          <HelperText>{helperText}</HelperText>
          <HelperText>
            <strong>
              The receiving sale must not have any bidders registered yet.
            </strong>
          </HelperText>
          <Grid container spacing={2}>
            {isFetchingSales && saleOptions.length === 0 && (
              <Grid item xs={12}>
                Loading sales...
              </Grid>
            )}
            {!isFetchingSales && saleOptions.length === 0 && (
              <Grid item xs={12}>
                No sales are available.
              </Grid>
            )}
            {saleOptions.length > 0 && (
              <Grid item xs={12}>
                <Label tooltip={tooltip}>Sale</Label>
                <Autocomplete
                  data-tour="copy-bidder-registrations-sale"
                  autoHighlight
                  getOptionLabel={(saleOption: CopyBidderSaleOption) =>
                    saleOption.label
                  }
                  getOptionSelected={(
                    saleOption: CopyBidderSaleOption,
                    value: CopyBidderSaleOption,
                  ) => saleOption.value === value.value}
                  onChange={(
                    ignored,
                    saleOption: CopyBidderSaleOption | null,
                  ): void => setSelectedSaleId(saleOption?.value || null)}
                  options={saleOptions}
                  renderInput={params => (
                    <TextField {...params} placeholder="Select sale..." />
                  )}
                  renderOption={renderSaleOption}
                  value={selectedSaleOption}
                />
              </Grid>
            )}
            {!allSalesLoaded && (
              <Grid item xs={12}>
                <SlimButton
                  fullWidth
                  disabled={isFetchingSales || !isOnline}
                  onClick={loadAllSales}
                >
                  {LOAD_MORE_SALES}
                </SlimButton>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={onClose} type="button">
            Cancel
          </SecondaryButton>
          <Button
            data-tour="copy-bidder-registrations-submit"
            disabled={!selectedSaleId || !isOnline}
            onClick={() => setIsConfirmOpen(true)}
            type="button"
          >
            Copy Bidders
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        title="Confirm Copy Bidders"
        isOpen={isConfirmOpen}
        onCancel={() => setIsConfirmOpen(false)}
        onDelete={onConfirm}
        message={
          <span>
            All bidder registrations will be copied from{" "}
            <strong>{fromSaleName}</strong> to <strong>{toSaleName}</strong>.
          </span>
        }
        buttonMessage="Copy Bidders"
      />
    </>
  );
}
